import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { CheckCircle, Link, Server, BarChart3, AlertTriangle, Ship, Plane } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

// 船司图标组件
function CarrierIcons({ show }: { show: boolean }) {
  const carriers = [
    { name: "MSK", color: "#00A3E0" },
    { name: "COSCO", color: "#E31937" },
    { name: "CMA", color: "#00205B" },
    { name: "EMC", color: "#00A651" },
  ];

  return (
    <div className="flex gap-1.5 justify-center mt-2 h-8">
      {carriers.map((carrier, idx) => (
        <AnimatePresence key={carrier.name}>
          {show && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.15 }}
              className="w-7 h-7 rounded-md flex items-center justify-center text-[8px] font-bold text-white"
              style={{ backgroundColor: carrier.color }}
            >
              {carrier.name}
            </motion.div>
          )}
        </AnimatePresence>
      ))}
    </div>
  );
}

// API 数据流动画
function ApiDataFlow({ show }: { show: boolean }) {
  return (
    <div className="relative h-8 mt-2 overflow-hidden">
      {show && (
        <>
          {/* 固定直线 */}
          <div className="absolute inset-x-2 top-1/2 h-0.5 bg-emerald-400/30 rounded-full" />
          
          {/* 沿直线移动的光点 */}
          {[...Array(2)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ left: 0, opacity: 0 }}
              animate={{ 
                left: ["0%", "100%"],
                opacity: [0, 1, 1, 0]
              }}
              transition={{
                duration: 1.5,
                delay: i * 0.7,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-emerald-400"
              style={{
                boxShadow: "0 0 8px rgba(16, 185, 129, 0.8), 0 0 12px rgba(16, 185, 129, 0.4)"
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}

// 容量预测折线图
function CapacityChart({ show }: { show: boolean }) {
  const points = [20, 35, 28, 45, 38, 52, 48];
  const pathD = points.map((y, i) => `${i === 0 ? 'M' : 'L'} ${i * 12 + 6} ${45 - y * 0.7}`).join(' ');

  return (
    <div className="relative h-12 mt-2">
      {show && (
        <svg className="w-full h-full" viewBox="0 0 90 50" preserveAspectRatio="xMidYMid meet">
          <motion.path
            d={pathD}
            fill="none"
            stroke="url(#chartGradient)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, ease: "easeInOut" }}
          />
          <defs>
            <linearGradient id="chartGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#10b981" />
              <stop offset="100%" stopColor="#14b8a6" />
            </linearGradient>
          </defs>
        </svg>
      )}
    </div>
  );
}

// 异常解决动效
function ExceptionResolved({ show }: { show: boolean }) {
  const { t } = useLanguage();
  return (
    <div className="flex items-center justify-center mt-2 h-8">
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.3, 1], opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="flex items-center gap-2"
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
              className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center"
            >
              <CheckCircle className="w-4 h-4 text-white" />
            </motion.div>
            <span className="text-xs text-emerald-400 font-medium">{t("bookingAnim.exceptionResolved")}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// 智能链路光带
function ConnectionBeam({ 
  from, 
  to, 
  isActive, 
  direction 
}: { 
  from: string; 
  to: string; 
  isActive: boolean;
  direction: 'horizontal' | 'vertical';
}) {
  return (
    <div className={`relative ${direction === 'horizontal' ? 'w-full h-3' : 'h-full w-3'}`}>
      {/* 基础链路线 */}
      <div className={`absolute ${direction === 'horizontal' ? 'inset-x-0 top-1/2 h-0.5' : 'inset-y-0 left-1/2 w-0.5'} bg-slate-700/50`} />
      
      {/* 活跃光带 */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={direction === 'horizontal' ? { left: 0, width: 0 } : { top: 0, height: 0 }}
            animate={direction === 'horizontal' ? { left: [0, '100%'], width: ['0%', '40%', '0%'] } : { top: [0, '100%'], height: ['0%', '40%', '0%'] }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className={`absolute ${direction === 'horizontal' ? 'top-1/2 -translate-y-1/2 h-1' : 'left-1/2 -translate-x-1/2 w-1'} bg-gradient-to-r from-emerald-500/80 via-emerald-400 to-emerald-500/80 rounded-full blur-[1px]`}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// 功能模块
function FeatureModule({
  icon: Icon,
  title,
  isActive,
  isComplete,
  children,
  stats,
  showStats,
}: {
  icon: React.ElementType;
  title: string;
  isActive: boolean;
  isComplete: boolean;
  children?: React.ReactNode;
  stats?: { label: string; value: number; suffix?: string };
  showStats?: boolean;
}) {
  const { t } = useLanguage();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (showStats && stats) {
      const target = stats.value;
      const duration = 800;
      const steps = 30;
      const increment = target / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }
    return undefined;
  }, [showStats, stats]);

  return (
    <motion.div
      animate={{ 
        borderColor: isActive ? "rgba(16, 185, 129, 0.8)" : isComplete ? "rgba(16, 185, 129, 0.4)" : "rgba(71, 85, 105, 0.3)",
        boxShadow: isActive ? "0 0 20px rgba(16, 185, 129, 0.3)" : "none"
      }}
      transition={{ duration: 0.3 }}
      className="relative p-3 rounded-xl bg-slate-800/70 border-2 transition-all duration-300 h-[120px]"
    >
      {isActive && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          className="absolute inset-0 rounded-xl bg-emerald-500/10 pointer-events-none" 
        />
      )}
      
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive ? "bg-emerald-500/30" : "bg-slate-700/50"}`}>
            <Icon className={`w-4 h-4 ${isActive ? "text-emerald-400" : "text-slate-400"}`} />
          </div>
          <span className={`text-xs font-medium ${isActive ? "text-emerald-300" : "text-slate-300"}`}>{title}</span>
          
          {isComplete && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="ml-auto"
            >
              <CheckCircle className="w-4 h-4 text-emerald-400" />
            </motion.div>
          )}
        </div>
        
        {children}
        
        {showStats && stats && (
          <motion.div 
            initial={{ opacity: 0, y: 5 }} 
            animate={{ opacity: 1, y: 0 }}
            className="text-[10px] text-emerald-400 mt-1 text-center"
          >
            {stats.label}{count}{stats.suffix || ''}
          </motion.div>
        )}
      </div>

      {/* 提示气泡 */}
      <AnimatePresence>
        {isActive && stats?.label.includes('响应') && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap z-20"
          >
            <div className="px-2 py-1 text-[10px] font-medium text-emerald-300 bg-emerald-500/20 rounded-lg border border-emerald-400/30 backdrop-blur-xs">
              {t("bookingAnim.apiResponse")}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-emerald-500/20 border-r border-b border-emerald-400/30 rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function BookingEmployeeCard() {
  const { t } = useLanguage();
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const sequence = [
      { phase: 0, duration: 800 },   // 初始
      { phase: 1, duration: 2000 },  // 船司直连激活
      { phase: 2, duration: 800 },   // 光带流向 API
      { phase: 3, duration: 2000 },  // API 激活
      { phase: 4, duration: 800 },   // 光带流向预测
      { phase: 5, duration: 2000 },  // 预测激活
      { phase: 6, duration: 800 },   // 光带流向异常
      { phase: 7, duration: 2500 },  // 异常激活 + 闪烁收尾
      { phase: 8, duration: 1000 },  // 完成状态
    ];
    
    let currentIndex = 0;
    
    const runSequence = () => {
      const current = sequence[currentIndex]!;
      setPhase(current.phase);
      currentIndex = (currentIndex + 1) % sequence.length;
      setTimeout(runSequence, current.duration);
    };
    
    const timer = setTimeout(runSequence, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative h-full rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700/50 overflow-hidden flex flex-col">
      {/* 网格背景 */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px)`,
          backgroundSize: '25px 25px'
        }} />
      </div>
      
      {/* 顶部光晕 */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-emerald-500/10 blur-3xl rounded-full" />
      
      {/* 完成时的绿色光晕 */}
      <AnimatePresence>
        {phase === 7 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{ duration: 1.5 }}
            className="absolute inset-0 bg-emerald-500/20 pointer-events-none z-20"
          />
        )}
      </AnimatePresence>
      
      {/* 标题栏 */}
      <div className="relative z-10 p-4 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <motion.div 
            animate={{ 
              boxShadow: phase >= 1 ? "0 0 10px rgba(16, 185, 129, 0.5)" : "none"
            }}
            className="w-3 h-3 rounded-full bg-emerald-400"
          />
          <h3 className="text-base font-semibold text-slate-100">{t("bookingWorker.title")}</h3>
        </div>
      </div>
      
      {/* 主内容区 - 2x2 网格布局 */}
      <div className="relative z-10 p-4 flex-1">
        <div className="grid grid-cols-2 gap-3">
          {/* 船司/航司直连 */}
          <FeatureModule
            icon={Link}
            title={t("bookingWorker.h1")}
            isActive={phase === 1}
            isComplete={phase >= 2}
            stats={{ label: t("bookingAnim.carrierConnected"), value: 200, suffix: t("bookingAnim.carriers") }}
            showStats={phase >= 1}
          >
            <CarrierIcons show={phase >= 1} />
          </FeatureModule>

          {/* NVOCC 与数字货代 API */}
          <FeatureModule
            icon={Server}
            title={t("bookingWorker.h2")}
            isActive={phase === 3}
            isComplete={phase >= 4}
            stats={{ label: t("bookingAnim.responseSpeed"), value: 10, suffix: "ms" }}
            showStats={phase >= 3}
          >
            <ApiDataFlow show={phase >= 3} />
          </FeatureModule>

          {/* 容量预测 */}
          <FeatureModule
            icon={BarChart3}
            title={t("bookingWorker.h3")}
            isActive={phase === 5}
            isComplete={phase >= 6}
            stats={{ label: t("bookingAnim.predictionAccuracy"), value: 96, suffix: "%" }}
            showStats={phase >= 5}
          >
            <CapacityChart show={phase >= 5} />
          </FeatureModule>

          {/* 异常自动处理 */}
          <FeatureModule
            icon={AlertTriangle}
            title={t("bookingWorker.h4")}
            isActive={phase === 7}
            isComplete={phase >= 8}
            stats={{ label: t("bookingAnim.todayHandled"), value: 12, suffix: t("bookingAnim.times") }}
            showStats={phase >= 7}
          >
            <ExceptionResolved show={phase >= 7} />
          </FeatureModule>
        </div>

        {/* 环绕式链路 - 连接四个卡片 */}
        <svg className="absolute inset-4 pointer-events-none z-0" viewBox="0 0 200 200" preserveAspectRatio="none" style={{ width: 'calc(100% - 32px)', height: 'calc(100% - 32px)' }}>
          {/* 链路路径 - 顺时针连接 */}
          <path
            d="M 80 40 L 160 40 Q 180 40 180 60 L 180 140 Q 180 160 160 160 L 80 160 Q 60 160 60 140 L 60 60 Q 60 40 80 40"

            fill="none"
            stroke="rgba(71, 85, 105, 0.3)"
            strokeWidth="2"
            strokeDasharray="4 4"
          />
          
          {/* 流动光点 */}
          {phase >= 2 && phase <= 7 && (
            <motion.circle
              r="4"
              fill="#10b981"
              filter="url(#glow)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <animateMotion
                dur="4s"
                repeatCount="indefinite"
                path="M 80 40 L 160 40 Q 180 40 180 60 L 180 140 Q 180 160 160 160 L 80 160 Q 60 160 60 140 L 60 60 Q 60 40 80 40"
              />
            </motion.circle>
          )}
          
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
      </div>
      
      {/* 底部渐变 */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none" />
    </div>
  );
}